
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Stack,
    IconButton,
    InputAdornment,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Tabs,
    Tab,
    Avatar,
    Checkbox,
    Select,
    Breadcrumbs,
    useTheme,
    useMediaQuery,
    CircularProgress,
    Snackbar,
    Alert
} from '@mui/material';
import { 
    Search as SearchIcon, 
    Description as DescriptionIcon, 
    KeyboardArrowDown as KeyboardArrowDownIcon,
    Close as CloseIcon,
    Add as AddIcon,
    Print as PrintIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import axios from 'axios';
import { getApiUrl, getUploadUrl } from '../config/api';
import { getGrades, SchoolType, getLetterGrade } from '../utils/gradeUtils';
// recharts removed (unused)
import { useReactToPrint } from 'react-to-print';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';

// REMOVED: const GRADES = [...]

interface Student {
    id: string;
    studentId: string;
    firstName: string;
    lastName: string;
    dob: string;
    gender: string;
    grade: string;
    class: string;
    department?: string;
    status: string;
    yearOfEnrollment: number;
    emergencyContactName: string;
    emergencyContactRelation: string;
    emergencyContactPhone: string;
    guardian: {
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        address: string;
        workAddress?: string;
        workPhone?: string;
        occupation?: string;
        relationToStudent: string;
    };
    balance: number;
    invoices?: any[];
    PosOrder?: any[];
    createdAt: string;
}

const StudentsPage = () => {
    const navigate = useNavigate();
    const [students, setStudents] = useState<Student[]>([]);
    const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [user, setUser] = useState<any>(null);
    const [schoolType, setSchoolType] = useState<SchoolType>('PRIMARY');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // List navigation states
    const [listTab, setListTab] = useState(0); // 0: ALL, 1: PRESENT, 2: ALUMNI
    const [searchTerm, setSearchTerm] = useState('');
    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [reportData, setReportData] = useState<any>(null);
    const [loadingReport, setLoadingReport] = useState(false);
    const [school, setSchool] = useState<any>(null);
    const [statementDialogOpen, setStatementDialogOpen] = useState(false);
    const [statementData, setStatementData] = useState<any>(null);
    const [loadingStatement, setLoadingStatement] = useState(false);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

    // Printing Setup
    const reportRef = useRef<HTMLDivElement>(null);
    const handlePrint = useReactToPrint({
        contentRef: reportRef,
        documentTitle: `Report_Card_${selectedStudent?.studentId || 'Student'}`
    });

    // Download PDF logic
    const handleDownloadPDF = async () => {
        if (!reportRef.current) return;
        try {
            const canvas = await html2canvas(reportRef.current, { scale: 2 });
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'mm', 'a4');
            const imgProps = pdf.getImageProperties(imgData);
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
            pdf.save(`Report_Card_${selectedStudent?.studentId}.pdf`);
        } catch (err) {
            console.error('PDF Generation failed:', err);
        }
    };

    // Filter states
    const [genderFilter, setGenderFilter] = useState('Gender');
    const [gradeFilter, setGradeFilter] = useState('Grade');
    const [classFilter, setClassFilter] = useState('Class');
    const [searchParams] = useSearchParams();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        dob: '',
        gender: 'Male',
        grade: 'Grade 1',
        class: '',
        status: 'PRESENT',
        department: 'Science',
        yearOfEnrollment: new Date().getFullYear(),
        emergencyContactName: '',
        emergencyContactRelation: '',
        emergencyContactPhone: '',
        guardian: {
            firstName: '',
            lastName: '',
            email: '',
            phone: '',
            address: '',
            workAddress: '',
            workPhone: '',
            occupation: '',
            relationToStudent: 'Father'
        }
    });

    const fetchStudents = useCallback(async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            const res = await axios.get(getApiUrl('students'), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStudents(res.data);
            // Only auto-select if nothing is selected yet
            setSelectedStudent(prev => {
                if (res.data.length > 0 && !prev) return res.data[0];
                return prev;
            });
        } catch (error: any) {
            console.error('Fetch error:', error.response?.data?.message || error.message);
        }
    }, []); // Removed selectedStudent dependency

    useEffect(() => {
        const storedUserInfo = localStorage.getItem('userInfo');
        if (storedUserInfo) {
            const parsed = JSON.parse(storedUserInfo);
            setUser(parsed.user || parsed); // Handle different return shapes
        }

        const fetchSchoolProfile = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const { data } = await axios.get(getApiUrl('school'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSchoolType(data.schoolType || 'PRIMARY');
            } catch (err) {
                console.error('Error fetching school profile:', err);
            }
        };

        const fetchSchool = async () => {
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const { data } = await axios.get(getApiUrl('school'), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setSchool(data);
            } catch (err) {
                console.error('Error fetching school profile:', err);
            }
        };
        fetchSchool();

        fetchSchoolProfile();
        fetchStudents();
    }, [fetchStudents]);

    // Fetch full details when selected student changes
    useEffect(() => {
        const fetchDetails = async () => {
            if (!selectedStudent?.id) return;
            try {
                const userInfo = localStorage.getItem('userInfo');
                const token = userInfo ? JSON.parse(userInfo).token : null;
                const res = await axios.get(getApiUrl(`students/${selectedStudent.id}`), {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Let's just update.
                setSelectedStudent(prev => ({ ...prev, ...res.data }));
            } catch (error) {
                console.error('Error fetching details:', error);
            }
        };

        if (selectedStudent?.id && !selectedStudent.invoices) {
            fetchDetails();
        }
    }, [selectedStudent?.id, selectedStudent?.invoices]);

    const handleSaveStudent = async () => {
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (isEditMode && selectedStudent) {
                await axios.put(getApiUrl(`students/${selectedStudent.id}`), formData, config);
            } else {
                await axios.post(getApiUrl('students'), formData, config);
            }

            setOpenDialog(false);
            fetchStudents();
        } catch (error: any) {
            console.error('Save error:', error.response?.data?.message || error.message);
            alert(error.response?.data?.message || 'Failed to save student');
        }
    };

    const openCreateDialog = () => {
        setIsEditMode(false);
        setFormData({
            firstName: '', lastName: '', dob: '', gender: 'Male', grade: 'Grade 1', class: '',
            status: 'PRESENT', department: 'Science', yearOfEnrollment: new Date().getFullYear(),
            emergencyContactName: '', emergencyContactRelation: '', emergencyContactPhone: '',
            guardian: {
                firstName: '', lastName: '', email: '', phone: '', address: '',
                workAddress: '', workPhone: '', occupation: '', relationToStudent: 'Father'
            }
        });
        setOpenDialog(true);
    };

    useEffect(() => {
        const action = searchParams.get('action');
        if (action === 'create' || action === 'new') {
            openCreateDialog();
        }
    }, [searchParams]);

    const openEditDialog = (student: Student) => {
        setIsEditMode(true);
        setFormData({
            firstName: student.firstName,
            lastName: student.lastName,
            dob: student.dob.split('T')[0],
            gender: student.gender,
            grade: student.grade,
            class: student.class,
            status: student.status,
            department: student.department || 'Science',
            yearOfEnrollment: student.yearOfEnrollment,
            emergencyContactName: student.emergencyContactName,
            emergencyContactRelation: student.emergencyContactRelation,
            emergencyContactPhone: student.emergencyContactPhone,
            guardian: {
                firstName: student.guardian.firstName,
                lastName: student.guardian.lastName,
                email: student.guardian.email,
                phone: student.guardian.phone,
                address: student.guardian.address,
                workAddress: student.guardian.workAddress || '',
                workPhone: student.guardian.workPhone || '',
                occupation: student.guardian.occupation || '',
                relationToStudent: student.guardian.relationToStudent
            }
        });
        setOpenDialog(true);
    };

    const handleChangeStatus = async (status: string) => {
        if (!selectedStudent) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            const response = await axios.put(getApiUrl(`students/${selectedStudent.id}`), { status }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedStudent(response.data);
            fetchStudents(); // Refresh list
        } catch (error) {
            console.error('Error updating status:', error);
            alert('Failed to update status');
        }
    };

    const handleResendWelcomeEmail = async () => {
        if (!selectedStudent) return;
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;

            if (!token) {
                setSnackbar({ open: true, message: 'Authentication token not found. Please log in again.', severity: 'error' });
                return;
            }

            await axios.post(getApiUrl(`students/${selectedStudent.id}/email/welcome`), {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSnackbar({ open: true, message: `Welcome email resent to ${selectedStudent.guardian?.email || 'parent'} successfully!`, severity: 'success' });
        } catch (error) {
            console.error('Error resending email:', error);
            setSnackbar({ open: true, message: 'Failed to resend welcome email. Please try again.', severity: 'error' });
        }
    };


    const handleExportCSV = () => {
        if (students.length === 0) return;

        const headers = ['Student ID', 'First Name', 'Last Name', 'Gender', 'Grade', 'Class', 'Status', 'Guardian Name', 'Guardian Phone'];
        const csvContent = [
            headers.join(','),
            ...students.map(s => [
                s.studentId,
                s.firstName,
                s.lastName,
                s.gender,
                s.grade,
                s.class,
                s.status,
                `${s.guardian.firstName} ${s.guardian.lastName}`,
                s.guardian.phone
            ].map(v => `"${v}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `students_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleViewReport = async () => {
        if (!selectedStudent) return;
        setLoadingReport(true);
        setReportDialogOpen(true);
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl(`grades/report-card/${selectedStudent.id}`), {
                params: { term: 'Term 1', academicYear: new Date().getFullYear() },
                headers: { Authorization: `Bearer ${token}` }
            });
            setReportData(res.data);
        } catch (error) {
            console.error('Error fetching report:', error);
        } finally {
            setLoadingReport(false);
        }
    };

    const handleViewStatement = async () => {
        if (!selectedStudent) return;
        setLoadingStatement(true);
        setStatementDialogOpen(true);
        try {
            const userInfo = localStorage.getItem('userInfo');
            const token = userInfo ? JSON.parse(userInfo).token : null;
            const res = await axios.get(getApiUrl(`students/${selectedStudent.id}/statement`), {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStatementData(res.data);
        } catch (error) {
            console.error('Error fetching statement:', error);
            setSnackbar({ open: true, message: 'Failed to fetch financial statement', severity: 'error' });
        } finally {
            setLoadingStatement(false);
        }
    };

    const handleInput = (e: any) => {
        const { name, value } = e.target;
        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData((prev: any) => ({
                ...prev,
                [parent]: { ...prev[parent], [child]: value }
            }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const filteredStudents = students.filter(s => {
        const matchesSearch = (`${s.firstName} ${s.lastName}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
            s.studentId.toLowerCase().includes(searchTerm.toLowerCase()));

        const matchesGender = (genderFilter === 'Gender' || s.gender === genderFilter);
        const matchesGrade = (gradeFilter === 'Grade' || s.grade === gradeFilter);
        const matchesClass = (classFilter === 'Class' || s.class.toLowerCase().includes(classFilter.toLowerCase()));

        const matchesTab = listTab === 0 ||
            (listTab === 1 && (s.status === 'PRESENT' || s.status === 'ACTIVE')) ||
            (listTab === 2 && s.status === 'ALUMNI');

        return matchesSearch && matchesGender && matchesGrade && matchesClass && matchesTab;
    });

    const presentCount = students.filter(s => s.status === 'PRESENT' || s.status === 'ACTIVE').length;
    const alumniCount = students.filter(s => s.status === 'ALUMNI').length;

    return (
        <Box sx={{ p: 4, bgcolor: '#F4F7FE', minHeight: 'calc(100vh - 64px)' }}>
            {/* Header & Breadcrumbs */}
            <Stack direction={isMobile ? "column" : "row"} justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={2} spacing={isMobile ? 1 : 0}>
                <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} fontWeight={800} color="#263B7C">Students</Typography>
                    <Typography variant="body2" color="text.secondary">Manage student enrollment and records</Typography>
                </Box>
                {!isMobile && (
                    <Breadcrumbs separator="/" aria-label="breadcrumb">
                        <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>...Administrator</Typography>
                        <Typography color="#F9B43F" sx={{ fontSize: '0.8rem', fontWeight: 600 }}>Students</Typography>
                    </Breadcrumbs>
                )}
            </Stack>

            <Stack direction={isSmallMobile ? "column" : "row"} justifyContent="flex-end" spacing={2} mb={3}>
                <Stack direction="row" spacing={2} sx={{ width: isSmallMobile ? '100%' : 'auto' }}>
                    <Button
                        variant="outlined"
                        onClick={handlePrint}
                        fullWidth={isSmallMobile}
                        sx={{ borderRadius: '8px', color: '#718096', borderColor: '#E2E8F0', px: 3 }}
                    >
                        PRINT
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleExportCSV}
                        fullWidth={isSmallMobile}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{ borderRadius: '8px', color: '#718096', borderColor: '#E2E8F0', px: 3 }}
                    >
                        EXPORT
                    </Button>
                </Stack>
                {(user?.role !== 'PARENT' && user?.role !== 'STUDENT' && !!user) && (
                    <Button
                        variant="contained"
                        onClick={openCreateDialog}
                        fullWidth={isSmallMobile}
                        sx={{
                            bgcolor: '#263B7C',
                            '&:hover': { bgcolor: '#1a285a' },
                            borderRadius: '8px',
                            px: 4,
                            py: 1.5,
                            fontWeight: 600
                        }}
                    >
                        CREATE STUDENT
                    </Button>
                )}
            </Stack>

            <Grid container spacing={3}>
                {/* Left Side: Student List List */}
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #edf2f7', overflow: 'hidden' }}>
                        {/* Filters Row */}
                        <Box sx={{ p: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                            <TextField
                                placeholder="Search Name or ID..."
                                size="small"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                    sx: { borderRadius: '8px', bgcolor: 'white', width: isMobile ? '100%' : '220px' }
                                }}
                                sx={{ width: isMobile ? '100%' : 'auto' }}
                            />
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', width: isMobile ? '100%' : 'auto' }}>
                                <Select
                                    value={genderFilter}
                                    size="small"
                                    onChange={(e) => setGenderFilter(e.target.value)}
                                    sx={{ borderRadius: '8px', minWidth: 110, flexGrow: isMobile ? 1 : 0 }}
                                >
                                    <MenuItem value="Gender">Gender</MenuItem>
                                    <MenuItem value="Male">Male</MenuItem>
                                    <MenuItem value="Female">Female</MenuItem>
                                </Select>
                                <Select
                                    value={gradeFilter}
                                    size="small"
                                    onChange={(e) => setGradeFilter(e.target.value)}
                                    sx={{ borderRadius: '8px', minWidth: 120, flexGrow: isMobile ? 1 : 0 }}
                                >
                                    <MenuItem value="Grade">Grade</MenuItem>
                                    {getGrades(schoolType).map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                                </Select>
                                <TextField
                                    placeholder="Class..."
                                    size="small"
                                    value={classFilter === 'Class' ? '' : classFilter}
                                    onChange={(e) => setClassFilter(e.target.value)}
                                    sx={{
                                        '& .MuiOutlinedInput-root': { borderRadius: '8px', bgcolor: 'white', width: isMobile ? '100%' : '120px' },
                                        flexGrow: isMobile ? 1 : 0
                                    }}
                                />
                                <IconButton
                                    size="small"
                                    onClick={() => {
                                        setSearchTerm('');
                                        setGenderFilter('Gender');
                                        setGradeFilter('Grade');
                                        setClassFilter('Class');
                                    }}
                                    title="Reset Filters"
                                    sx={{ bgcolor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                                >
                                    <CloseIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        </Box>

                        {/* Tabs Bar */}
                        <Box sx={{ borderBottom: '1px solid #edf2f7', px: 3 }}>
                            <Tabs
                                value={listTab}
                                onChange={(_, v) => {
                                    setListTab(v);
                                    // Reset preferences on tab change as requested
                                    setSearchTerm('');
                                    setGenderFilter('Gender');
                                    setGradeFilter('Grade');
                                    setClassFilter('Class');
                                }}
                                sx={{
                                    '& .MuiTabs-indicator': { bgcolor: '#F9B43F', height: 3 },
                                    '& .MuiTab-root': { fontWeight: 600, color: '#718096', fontSize: '0.8rem' },
                                    '& .Mui-selected': { color: '#263B7C !important' }
                                }}
                            >
                                <Tab label={`ALL ${students.length}`} />
                                <Tab label={`PRESENT ${presentCount}`} />
                                <Tab label={`ALUMNI ${alumniCount}`} />
                            </Tabs>
                        </Box>

                        <Box sx={{ px: 3, py: 2 }}>
                            <Typography variant="caption" color="text.secondary">
                                Showing 1 - {filteredStudents.length} of {filteredStudents.length} students
                            </Typography>
                        </Box>

                        <TableContainer sx={{ maxHeight: '550px' }}>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell padding="checkbox" sx={{ bgcolor: 'white' }}><Checkbox size="small" /></TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#718096', fontSize: '0.75rem', bgcolor: 'white' }}>REF ID</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#718096', fontSize: '0.75rem', bgcolor: 'white' }}>FIRST NAME</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#718096', fontSize: '0.75rem', bgcolor: 'white' }}>LAST NAME</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#718096', fontSize: '0.75rem', bgcolor: 'white' }}>GENDER</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#718096', fontSize: '0.75rem', bgcolor: 'white' }}>GRADE</TableCell>
                                        <TableCell sx={{ fontWeight: 600, color: '#718096', fontSize: '0.75rem', bgcolor: 'white' }}>CLASS</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {filteredStudents.map((s) => (
                                        <TableRow
                                            key={s.id}
                                            hover
                                            onClick={() => setSelectedStudent(s)}
                                            selected={selectedStudent?.id === s.id}
                                            sx={{
                                                cursor: 'pointer',
                                                '&.Mui-selected': { bgcolor: '#F4F7FE' },
                                                '&:nth-of-type(even)': { bgcolor: '#FAFBFF' }
                                            }}
                                        >
                                            <TableCell padding="checkbox"><Checkbox size="small" checked={selectedStudent?.id === s.id} /></TableCell>
                                            <TableCell sx={{ color: '#263B7C', fontWeight: 500 }}>{s.studentId}</TableCell>
                                            <TableCell>{s.firstName}</TableCell>
                                            <TableCell>{s.lastName}</TableCell>
                                            <TableCell>{s.gender}</TableCell>
                                            <TableCell>{s.grade}</TableCell>
                                            <TableCell>{s.class}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                </Grid>

                {/* Right Side: Student details */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Paper elevation={0} sx={{ p: 0, borderRadius: '16px', border: '1px solid #edf2f7', bgcolor: 'white' }}>
                        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <IconButton size="small"><Avatar sx={{ width: 32, height: 32 }} src="/path/to/user.jpg" /></IconButton>
                                <Typography variant="body2" fontWeight={500} color="#2D3748">User</Typography>
                                <KeyboardArrowDownIcon sx={{ fontSize: '1rem', color: '#718096' }} />
                            </Box>
                        </Box>

                        <Box sx={{ px: 4, pb: 4 }}>
                            <Typography variant="h6" fontWeight={700} color="#2D3748" mb={3}>Student Details</Typography>

                            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                                <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ minHeight: 0 }}>
                                    <Tab label="BIO" sx={{ minHeight: 0, py: 1 }} />
                                    <Tab label="FINANCE" sx={{ minHeight: 0, py: 1 }} />
                                    <Tab label="REPORTS" sx={{ minHeight: 0, py: 1 }} />
                                </Tabs>
                            </Box>

                            {tabValue === 0 && selectedStudent && (
                                <Stack spacing={2.5}>
                                    <DetailField label="REF ID" value={selectedStudent.studentId} />
                                    <DetailField label="FIRST_NAME" value={selectedStudent.firstName} />
                                    <DetailField label="LAST_NAME" value={selectedStudent.lastName} />
                                    <DetailField label="GENDER" value={selectedStudent.gender} />
                                    <DetailField label="GRADE" value={selectedStudent.grade} />
                                    <DetailField label="CLASS" value={selectedStudent.class} />

                                    <Divider sx={{ my: 1 }} />
                                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#263B7C' }}>PARENT INFORMATION</Typography>

                                    <DetailField label="GUARDIAN NAME" value={`${selectedStudent.guardian.firstName} ${selectedStudent.guardian.lastName}`} />
                                    <DetailField label="PHONE" value={selectedStudent.guardian.phone} />
                                    <DetailField label="EMAIL" value={selectedStudent.guardian.email.toLowerCase()} color="#2EAADC" />
                                    <DetailField label="ADDRESS" value={selectedStudent.guardian.address} />

                                    <Divider sx={{ my: 1 }} />

                                    <DetailField
                                        label="DATE CREATED"
                                        value={new Date(selectedStudent.createdAt).toLocaleDateString('en-GB', {
                                            day: '2-digit', month: 'long', year: 'numeric'
                                        }).toUpperCase()}
                                    />
                                    <Box>
                                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#A0AEC0', mb: 0.5 }}>STUDENT STATUS</Typography>
                                        <Typography
                                            sx={{
                                                fontSize: '0.9rem',
                                                fontWeight: 800,
                                                color: selectedStudent.status === 'PRESENT' ? '#48BB78' :
                                                    selectedStudent.status === 'ALUMNI' ? '#263B7C' : '#E53E3E',
                                                bgcolor: selectedStudent.status === 'PRESENT' ? '#F0FFF4' :
                                                    selectedStudent.status === 'ALUMNI' ? '#EBF8FF' : '#FFF5F5',
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: '6px',
                                                display: 'inline-block'
                                            }}
                                        >
                                            {selectedStudent.status}
                                        </Typography>
                                    </Box>

                                    {(user?.role !== 'PARENT' && user?.role !== 'STUDENT' && !!user) && (
                                        <Stack spacing={2} pt={2}>
                                            <Stack direction="row" spacing={2}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    onClick={() => openEditDialog(selectedStudent)}
                                                    sx={{ borderRadius: '8px', color: '#263B7C', borderColor: '#263B7C', fontWeight: 600 }}
                                                >
                                                    EDIT BIO
                                                </Button>

                                                <Select
                                                    fullWidth
                                                    size="small"
                                                    value={selectedStudent.status}
                                                    onChange={(e) => handleChangeStatus(e.target.value)}
                                                    sx={{ borderRadius: '8px', fontWeight: 600, fontSize: '0.875rem' }}
                                                >
                                                    <MenuItem value="PRESENT">SET AS PRESENT</MenuItem>
                                                    <MenuItem value="ACTIVE">SET AS ACTIVE</MenuItem>
                                                    <MenuItem value="SUSPENDED">SET AS SUSPENDED</MenuItem>
                                                    <MenuItem value="EXPELLED">SET AS EXPELLED</MenuItem>
                                                    <MenuItem value="ALUMNI">SET AS ALUMNI</MenuItem>
                                                </Select>
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" textAlign="center">
                                                * Students cannot be deleted. Change status to manage lifecycle.
                                            </Typography>
                                            <Stack direction="row" spacing={2} pt={2}>
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    startIcon={<EmailIcon />}
                                                    onClick={handleResendWelcomeEmail}
                                                    sx={{ borderRadius: '8px', color: '#263B7C', borderColor: '#263B7C', fontWeight: 600, fontSize: '0.75rem' }}
                                                >
                                                    RESEND WELCOME EMAIL
                                                </Button>
                                                {selectedStudent.guardian?.phone && (
                                                    <Button
                                                        fullWidth
                                                        variant="text"
                                                        onClick={() => {
                                                            const message = `Hello ${selectedStudent.guardian.firstName}, welcome to SquareEdge School. Your child ${selectedStudent.firstName} has been registered using ID: ${selectedStudent.studentId}.`;
                                                            window.open(`https://wa.me/${selectedStudent.guardian.phone.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
                                                        }}
                                                        sx={{ borderRadius: '8px', color: '#25D366', fontWeight: 600, fontSize: '0.75rem' }}
                                                    >
                                                        WHATSAPP PARENT
                                                    </Button>
                                                )}
                                            </Stack>
                                        </Stack>
                                    )}
                                </Stack>
                            )}

                            {tabValue === 1 && selectedStudent && (
                                <Stack spacing={3}>
                                    <Box sx={{ p: 3, bgcolor: '#f8f9fa', borderRadius: '12px', textAlign: 'center', border: '1px solid #edf2f7' }}>
                                        <Typography variant="h5" fontWeight={700} color={(selectedStudent.balance || 0) > 0 ? 'error.main' : 'success.main'}>
                                            ${Number(selectedStudent.balance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">Current Balance</Typography>
                                    </Box>

                                    {/* Recent Invoices list */}
                                    <Box>
                                        <Typography variant="caption" fontWeight={700} color="#A0AEC0" sx={{ mb: 1, display: 'block' }}>RECENT INVOICES</Typography>
                                        {selectedStudent.invoices && selectedStudent.invoices.length > 0 ? (
                                            <Stack spacing={1}>
                                                {selectedStudent.invoices.map((inv: any) => (
                                                    <Box key={inv.id} sx={{ p: 1.5, border: '1px solid #edf2f7', borderRadius: '8px', bgcolor: 'white' }}>
                                                        <Stack direction="row" justifyContent="space-between">
                                                            <Typography variant="body2" fontWeight={600}>{inv.invoiceNo}</Typography>
                                                            <Typography variant="body2" fontWeight={700}>${Number(inv.totalAmount).toLocaleString()}</Typography>
                                                        </Stack>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                                                            <Typography variant="caption" color="text.secondary">{new Date(inv.createdAt).toLocaleDateString()}</Typography>
                                                            <Box sx={{
                                                                bgcolor: inv.status === 'PAID' ? '#F0FFF4' : '#FFF5F5',
                                                                color: inv.status === 'PAID' ? '#48BB78' : '#E53E3E',
                                                                px: 1, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700
                                                            }}>
                                                                {inv.status}
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No billing history available.</Typography>
                                        )}
                                    </Box>

                                    {/* Recent POS Transactions */}
                                    <Box>
                                        <Typography variant="caption" fontWeight={700} color="#A0AEC0" sx={{ mb: 1, display: 'block' }}>RECENT POS TRANSACTIONS</Typography>
                                        {selectedStudent.PosOrder && selectedStudent.PosOrder.length > 0 ? (
                                            <Stack spacing={1}>
                                                {selectedStudent.PosOrder.map((order: any) => (
                                                    <Box key={order.id} sx={{ p: 1.5, border: '1px solid #edf2f7', borderRadius: '8px', bgcolor: 'white' }}>
                                                        <Stack direction="row" justifyContent="space-between">
                                                            <Typography variant="body2" fontWeight={600}>{order.orderNo}</Typography>
                                                            <Typography variant="body2" fontWeight={700}>${Number(order.totalAmount).toLocaleString()}</Typography>
                                                        </Stack>
                                                        <Stack direction="row" justifyContent="space-between" alignItems="center" mt={0.5}>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                            </Typography>
                                                            <Box sx={{
                                                                bgcolor: '#F0FFF4',
                                                                color: '#48BB78',
                                                                px: 1, borderRadius: '4px', fontSize: '0.65rem', fontWeight: 700
                                                            }}>
                                                                {order.paymentMethod.replace('_', ' ')}
                                                            </Box>
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>No POS history available.</Typography>
                                        )}
                                    </Box>

                                    <Stack direction="row" spacing={2}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => navigate(`/dashboard/invoices?studentId=${selectedStudent.id}&action=new`)}
                                            startIcon={<AddIcon />}
                                            sx={{ bgcolor: '#263B7C', borderRadius: '8px', fontSize: '0.75rem' }}
                                        >
                                            CREATE INVOICE
                                        </Button>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            onClick={() => navigate(`/dashboard/payments?studentId=${selectedStudent.id}&action=new`)}
                                            startIcon={<AccountBalanceWalletIcon />}
                                            sx={{ bgcolor: '#F9B43F', borderRadius: '8px', fontSize: '0.75rem' }}
                                        >
                                            RECORD PAYMENT
                                        </Button>
                                    </Stack>

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        onClick={() => navigate('/dashboard/payments')}
                                        sx={{ borderRadius: '8px', borderColor: '#263B7C', color: '#263B7C', fontSize: '0.75rem' }}
                                    >
                                        Full Payment History
                                    </Button>
                                </Stack>
                            )}

                            {tabValue === 2 && (
                                <Stack spacing={2}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#F8FAFC' }}>
                                        <Typography variant="subtitle2" fontWeight={700} color="#2D3748" mb={1}>ACADEMIC PERFORMANCE</Typography>
                                        <Typography variant="body2" color="text.secondary" mb={2}>View comprehensive report card including grades and attendance.</Typography>
                                        <Button 
                                            fullWidth 
                                            variant="contained" 
                                            startIcon={<DescriptionIcon />} 
                                            onClick={handleViewReport}
                                            sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}
                                        >
                                            VIEW REPORT CARD
                                        </Button>
                                    </Paper>
                                    <Button 
                                        fullWidth 
                                        variant="text" 
                                        startIcon={<DescriptionIcon />} 
                                        onClick={handleViewStatement}
                                        sx={{ justifyContent: 'start', color: '#4A5568', fontWeight: 600, mt: 1 }}
                                    >
                                        Financial Statement
                                    </Button>
                                </Stack>
                            )}
                        </Box>
                    </Paper>
                </Grid>
            </Grid>

            {/* Create Student Dialog Placeholder */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {isEditMode ? 'Update Student Record' : 'Enroll New Student'}
                    <IconButton onClick={() => setOpenDialog(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <Grid container spacing={3} mt={1}>
                        {/* Section 1: Student Information */}
                        <Grid size={12}><Typography variant="subtitle1" fontWeight={700} color="#263B7C">Student Information</Typography><Divider /></Grid>

                        <Grid size={6}><TextField fullWidth label="First Name" name="firstName" value={formData.firstName} onChange={handleInput} required /></Grid>
                        <Grid size={6}><TextField fullWidth label="Last Name" name="lastName" value={formData.lastName} onChange={handleInput} required /></Grid>
                        <Grid size={4}><TextField fullWidth label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={handleInput} InputLabelProps={{ shrink: true }} required /></Grid>
                        <Grid size={4}>
                            <TextField fullWidth select label="Gender" name="gender" value={formData.gender} onChange={handleInput}>
                                <MenuItem value="Male">Male</MenuItem>
                                <MenuItem value="Female">Female</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={4}>
                            <TextField fullWidth label="Year of Enrollment" name="yearOfEnrollment" type="number" value={formData.yearOfEnrollment} onChange={handleInput} />
                        </Grid>
                        <Grid size={6}>
                            <TextField fullWidth select label="Grade" name="grade" value={formData.grade} onChange={handleInput}>
                                {getGrades(schoolType).map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid size={6}><TextField fullWidth label="Class (e.g., 7 Orange)" name="class" value={formData.class} onChange={handleInput} required /></Grid>

                        {/* Section 2: Emergency Contact */}
                        <Grid size={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight={700} color="#263B7C">Emergency Contact</Typography><Divider /></Grid>
                        <Grid size={4}><TextField fullWidth label="Contact Name" name="emergencyContactName" value={formData.emergencyContactName} onChange={handleInput} /></Grid>
                        <Grid size={4}><TextField fullWidth label="Relation" name="emergencyContactRelation" value={formData.emergencyContactRelation} onChange={handleInput} /></Grid>
                        <Grid size={4}><TextField fullWidth label="Phone Number" name="emergencyContactPhone" value={formData.emergencyContactPhone} onChange={handleInput} /></Grid>

                        {/* Section 3: Primary Guardian Information (KYC) */}
                        <Grid size={12} sx={{ mt: 2 }}><Typography variant="subtitle1" fontWeight={700} color="#263B7C">Guardian Information (KYC)</Typography><Divider /></Grid>
                        <Grid size={6}><TextField fullWidth label="Guardian First Name" name="guardian.firstName" value={formData.guardian.firstName} onChange={handleInput} required /></Grid>
                        <Grid size={6}><TextField fullWidth label="Guardian Last Name" name="guardian.lastName" value={formData.guardian.lastName} onChange={handleInput} required /></Grid>
                        <Grid size={6}><TextField fullWidth label="Email Address" name="guardian.email" type="email" value={formData.guardian.email} onChange={handleInput} required /></Grid>
                        <Grid size={6}><TextField fullWidth label="Primary Phone" name="guardian.phone" value={formData.guardian.phone} onChange={handleInput} required /></Grid>
                        <Grid size={6}>
                            <TextField fullWidth select label="Relation to Student" name="guardian.relationToStudent" value={formData.guardian.relationToStudent} onChange={handleInput}>
                                <MenuItem value="Father">Father</MenuItem>
                                <MenuItem value="Mother">Mother</MenuItem>
                                <MenuItem value="Brother">Brother</MenuItem>
                                <MenuItem value="Sister">Sister</MenuItem>
                                <MenuItem value="Legal Guardian">Legal Guardian</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid size={6}><TextField fullWidth label="Occupation" name="guardian.occupation" value={formData.guardian.occupation} onChange={handleInput} /></Grid>
                        <Grid size={12}><TextField fullWidth label="Residential Address" name="guardian.address" multiline rows={2} value={formData.guardian.address} onChange={handleInput} required /></Grid>
                        <Grid size={6}><TextField fullWidth label="Work Address" name="guardian.workAddress" value={formData.guardian.workAddress} onChange={handleInput} /></Grid>
                        <Grid size={6}><TextField fullWidth label="Work Phone" name="guardian.workPhone" value={formData.guardian.workPhone} onChange={handleInput} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: '1px solid #edf2f7' }}>
                    <Button onClick={() => setOpenDialog(false)} color="inherit">Cancel</Button>
                    <Button
                        onClick={handleSaveStudent}
                        variant="contained"
                        sx={{ bgcolor: '#F9B43F', '&:hover': { bgcolor: '#e0a139' }, color: 'white', fontWeight: 600, px: 4 }}
                    >
                        {isEditMode ? 'Save Changes' : 'Complete Enrollment'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Student Report Dialog */}
            <Dialog 
                open={reportDialogOpen} 
                onClose={() => setReportDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: '24px', overflow: 'hidden' }
                }}
            >
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white', p: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h5" fontWeight={900}>OFFICIAL STUDENT REPORT CARD</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Academic Term Performance Summary</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} className="no-print">
                            <Button startIcon={<PrintIcon />} sx={{ color: 'white' }} onClick={() => handlePrint()}>Print</Button>
                            <IconButton onClick={() => setReportDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    <style>
                        {`
                            @media print {
                                body * { visibility: hidden; }
                                #report-card-printable, #report-card-printable * { visibility: visible; }
                                #report-card-printable {
                                    position: absolute;
                                    left: 0;
                                    top: 0;
                                    width: 100%;
                                    padding: 0;
                                    margin: 0;
                                    background: white !important;
                                    print-color-adjust: exact;
                                    -webkit-print-color-adjust: exact;
                                }
                                .no-print { display: none !important; }
                                .MuiPaper-root { border: 1px solid #E2E8F0 !important; box-shadow: none !important; }
                                @page { size: auto; margin: 15mm; }
                            }
                        `}
                    </style>
                    {loadingReport ? (
                        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                    ) : reportData ? (
                        <Box ref={reportRef} id="report-card-printable" sx={{ bgcolor: 'white', pb: 10 }}>
                            {/* Professional Branded Header */}
                            <Grid container spacing={3} alignItems="center" sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack direction="row" spacing={3} alignItems="center">
                                        {school?.logo && (
                                            <Box
                                                component="img"
                                                src={getUploadUrl(school.logo)}
                                                sx={{ height: 100, width: 100, objectFit: 'contain' }}
                                            />
                                        )}
                                        <Box>
                                            <Typography variant="h5" fontWeight={900} color="#263B7C" sx={{ letterSpacing: -0.5 }}>
                                                {school?.name || 'SquareEdge Academy'}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ maxWidth: 400, lineHeight: 1.4 }}>
                                                {school?.address}
                                                {'\n'}{school?.phone} | {school?.email}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid size={{ xs: 12, md: 5 }} sx={{ textAlign: 'right' }}>
                                    <Typography variant="h4" fontWeight={900} color="#F1F4F9" sx={{ letterSpacing: 2, mb: -1, opacity: 0.5 }}>
                                        REPORT CARD
                                    </Typography>
                                    <Typography variant="h6" fontWeight={900} color="#263B7C">
                                        TERM END RESULTS
                                    </Typography>
                                    <Typography variant="body2" fontWeight={800} color="#F9B43F">
                                        Academic Year: 2026 | Term 1
                                    </Typography>
                                </Grid>
                            </Grid>

                            {/* Student Header */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Paper variant="outlined" sx={{ p: 3, borderRadius: '16px', bgcolor: '#F8FAFC' }}>
                                        <Stack direction="row" spacing={3} alignItems="center">
                                            <Avatar sx={{ width: 80, height: 80, fontSize: '2rem', bgcolor: '#263B7C' }}>
                                                {selectedStudent?.firstName[0]}{selectedStudent?.lastName[0]}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="h6" fontWeight={900} color="#2D3748">
                                                    {selectedStudent?.firstName} {selectedStudent?.lastName}
                                                </Typography>
                                                <Typography variant="subtitle2" color="#4A5568" fontWeight={700}>ID: {selectedStudent?.studentId}</Typography>
                                                <Typography variant="caption" color="text.secondary" fontWeight={600}>Grade: {selectedStudent?.grade} | Class: {selectedStudent?.class}</Typography>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Box sx={{ p: 3, textAlign: 'right' }}>
                                        <Typography variant="h4" fontWeight={900} color="#263B7C">{reportData.summary.overallPercentage}%</Typography>
                                        <Typography variant="subtitle1" fontWeight={900} color={reportData.summary.overallPercentage >= 50 ? '#48BB78' : '#E53E3E'}>
                                            GRADE: {reportData.summary.grade}
                                        </Typography>
                                        <Typography variant="body2" fontWeight={800} color="#4A5568">
                                            RANK: {reportData.summary.rank} of {reportData.summary.classSize}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Performance Score</Typography>
                                    </Box>
                                </Grid>
                            </Grid>

                            {/* Grades Table */}
                            <Typography variant="h6" fontWeight={800} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                ACADEMIC TRANSCRIPT
                            </Typography>
                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '16px', mb: 4 }}>
                                <Table>
                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800 }}>SUBJECT</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>RAW SCORE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>PERCENTAGE</TableCell>
                                            <TableCell align="center" sx={{ fontWeight: 800 }}>GRADE</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>TEACHER FEEDBACK</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.grades.map((g: any) => (
                                            <TableRow key={g.id}>
                                                <TableCell sx={{ fontWeight: 700 }}>{g.subject.name}</TableCell>
                                                <TableCell align="center">{g.marks} / {g.maxMarks}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'inline-block', px: 1.5, py: 0.5, borderRadius: '8px', bgcolor: '#EBF4FF', color: '#4C51BF', fontWeight: 800 }}>
                                                        {((Number(g.marks) / Number(g.maxMarks)) * 100).toFixed(0)}%
                                                    </Box>
                                                </TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 900 }}>
                                                    {getLetterGrade((g.marks / g.maxMarks) * 100, selectedStudent?.grade)}
                                                </TableCell>
                                                <TableCell sx={{ color: 'text.secondary', fontSize: '0.875rem', fontStyle: 'italic' }}>
                                                    "{g.comment || 'No comment provided'}"
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Remarks Section */}
                            <Grid container spacing={3}>
                                <Grid size={12}>
                                    <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>OFFICIAL REMARKS</Typography>
                                    <Stack spacing={2}>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px' }}>
                                            <Typography variant="caption" fontWeight={800} color="#263B7C">TEACHER'S GENERAL COMMENT</Typography>
                                            <Typography variant="body2">{reportData.termReport?.teacherComment || 'Pending assessment'}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#FFF5F5' }}>
                                            <Typography variant="caption" fontWeight={800} color="#E53E3E">HOD'S COMMENT</Typography>
                                            <Typography variant="body2">{reportData.termReport?.hodComment || 'Pending review'}</Typography>
                                        </Paper>
                                        <Paper variant="outlined" sx={{ p: 2, borderRadius: '12px', bgcolor: '#F0FFF4' }}>
                                            <Typography variant="caption" fontWeight={800} color="#38A169">PRINCIPAL'S COMMENT</Typography>
                                            <Typography variant="body2">{reportData.termReport?.principalComment || 'Pending sign-off'}</Typography>
                                        </Paper>
                                    </Stack>
                                </Grid>
 
                                <Grid size={12}>
                                    <Divider sx={{ my: 3 }} />
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={800}>Attendance Standing</Typography>
                                            <Typography variant="h6" fontWeight={900} color="#263B7C">{reportData.attendance.percentage}%</Typography>
                                            <Typography variant="caption" color="text.secondary">{reportData.attendance.PRESENT} Days Present</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'center', minWidth: 200, mt: 4 }}>
                                            <Divider sx={{ mb: 1, borderColor: '#263B7C', borderBottomWidth: 2 }} />
                                            <Typography variant="caption" fontWeight={800} color="text.secondary" sx={{ fontSize: '0.65rem' }}>SCHOOL STAMP & PRINCIPAL'S SIGNATURE</Typography>
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Typography variant="subtitle2" fontWeight={800}>Next Term Information</Typography>
                                            <Typography variant="h6" fontWeight={900} color="#ED8936">
                                                {reportData.termReport?.nextTermDate ? new Date(reportData.termReport.nextTermDate).toLocaleDateString() : 'TBA'}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">SCHOOL REOPENS</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 10, pt: 4, borderTop: '1px solid #edf2f7', textAlign: 'center', opacity: 0.5 }}>
                                <Typography variant="caption">
                                    This is an official document generated by {school?.name || 'the School Management System'}. Any alteration renders it void.
                                    Generated on {new Date().toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography align="center" color="text.secondary">No report card data found.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 4, borderTop: '1px solid #edf2f7' }} className="no-print">
                    <Button variant="outlined" onClick={() => handlePrint()} sx={{ borderRadius: '8px' }}>PRINT REPORT</Button>
                    <Button 
                        variant="contained" 
                        sx={{ bgcolor: '#263B7C', borderRadius: '8px' }}
                        onClick={handleDownloadPDF}
                    >
                        DOWNLOAD PDF
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Financial Statement Dialog */}
            <Dialog 
                open={statementDialogOpen} 
                onClose={() => setStatementDialogOpen(false)}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: '24px' } }}
            >
                <DialogTitle sx={{ bgcolor: '#263B7C', color: 'white' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Box>
                            <Typography variant="h5" fontWeight={900}>STATEMENT OF ACCOUNT</Typography>
                            <Typography variant="body2" sx={{ opacity: 0.8 }}>Consolidated Billing & Payment History</Typography>
                        </Box>
                        <Stack direction="row" spacing={1} className="no-print">
                            <Button startIcon={<PrintIcon />} sx={{ color: 'white' }} onClick={() => handlePrint()}>Print</Button>
                            <IconButton onClick={() => setStatementDialogOpen(false)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
                        </Stack>
                    </Stack>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {loadingStatement ? (
                        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress /></Box>
                    ) : statementData ? (
                        <Box ref={reportRef} sx={{ bgcolor: 'white' }}>
                            {/* Branded Header */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                <Grid size={7}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        {school?.logo && <Box component="img" src={getUploadUrl(school.logo)} sx={{ height: 60 }} />}
                                        <Box>
                                            <Typography variant="h6" fontWeight={900} color="#263B7C">{school?.name}</Typography>
                                            <Typography variant="caption" sx={{ display: 'block' }}>{school?.address}</Typography>
                                            <Typography variant="caption">{school?.phone} | {school?.email}</Typography>
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid size={5} sx={{ textAlign: 'right' }}>
                                    <Typography variant="h6" fontWeight={900} color="#263B7C">OFFICIAL STATEMENT</Typography>
                                    <Typography variant="body2" fontWeight={700}>Date: {new Date().toLocaleDateString()}</Typography>
                                    <Typography variant="caption" color="text.secondary">Currency: {school?.currency || 'USD'}</Typography>
                                </Grid>
                            </Grid>

                            {/* Student Info Bar */}
                            <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: '12px', bgcolor: '#F8FAFC' }}>
                                <Grid container spacing={2}>
                                    <Grid size={4}><DetailField label="STUDENT" value={`${statementData.student.firstName} ${statementData.student.lastName}`} /></Grid>
                                    <Grid size={3}><DetailField label="ID" value={statementData.student.studentId} /></Grid>
                                    <Grid size={3}><DetailField label="GRADE" value={statementData.student.grade} /></Grid>
                                    <Grid size={2} sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" fontWeight={800} color="#A0AEC0">BALANCE</Typography>
                                        <Typography variant="h6" fontWeight={900} color="#E53E3E">
                                            ${Number(statementData.summary.currentBalance).toLocaleString()}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Paper>

                            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: '12px' }}>
                                <Table size="small">
                                    <TableHead sx={{ bgcolor: '#F8FAFC' }}>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 800 }}>DATE</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>REF #</TableCell>
                                            <TableCell sx={{ fontWeight: 800 }}>DESCRIPTION</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800 }}>DEBIT (+)</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800 }}>CREDIT (-)</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 800 }}>BALANCE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {statementData.ledger.map((tx: any) => (
                                            <TableRow key={tx.id}>
                                                <TableCell>{new Date(tx.date).toLocaleDateString()}</TableCell>
                                                <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem' }}>{tx.ref}</TableCell>
                                                <TableCell>{tx.description}</TableCell>
                                                <TableCell align="right" sx={{ color: tx.debit > 0 ? '#E53E3E' : 'inherit' }}>
                                                    {tx.debit > 0 ? `$${Number(tx.debit).toLocaleString()}` : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ color: tx.credit > 0 ? '#38A169' : 'inherit' }}>
                                                    {tx.credit > 0 ? `$${Number(tx.credit).toLocaleString()}` : '-'}
                                                </TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>
                                                    ${Number(tx.balance).toLocaleString()}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 4, textAlign: 'right' }}>
                                <Stack spacing={1} alignItems="flex-end">
                                    <Typography variant="body2">Total Billed: <strong>${Number(statementData.summary.totalBilled).toLocaleString()}</strong></Typography>
                                    <Typography variant="body2">Total Paid: <strong>${Number(statementData.summary.totalPaid).toLocaleString()}</strong></Typography>
                                    <Divider sx={{ width: 150, my: 1 }} />
                                    <Typography variant="h6" fontWeight={900} color="#263B7C">
                                        Amount Due: ${Number(statementData.summary.currentBalance).toLocaleString()}
                                    </Typography>
                                </Stack>
                            </Box>

                            <Box sx={{ mt: 10, textAlign: 'center', opacity: 0.5 }}>
                                <Typography variant="caption">This is a computer generated document. Generated at {new Date().toLocaleString()}</Typography>
                            </Box>
                        </Box>
                    ) : (
                        <Typography align="center">No statement data found.</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 4 }} className="no-print">
                    <Button variant="outlined" onClick={() => handlePrint()}>Print Statement</Button>
                    <Button variant="contained" sx={{ bgcolor: '#263B7C' }} onClick={handleDownloadPDF}>Download PDF</Button>
                </DialogActions>
            </Dialog>

            {/* Feedback Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box >
    );
};

const DetailField = ({ label, value, color }: { label: string, value: string, color?: string }) => (
    <Box>
        <Typography sx={{ fontSize: '0.65rem', fontWeight: 800, color: '#A0AEC0', mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: color || '#4A5568' }}>{value}</Typography>
    </Box>
);

export default StudentsPage;
